// src/utils/instructorDb.js
import { 
    collection, 
    addDoc, 
    updateDoc, 
    doc, 
    getDoc, 
    getDocs, 
    setDoc,
    deleteDoc,
    writeBatch,
    query, 
    where, 
    orderBy, 
    limit,
    serverTimestamp,
    Timestamp,
    onSnapshot,
    arrayUnion
  } from "firebase/firestore";
  import { httpsCallable } from "firebase/functions";
  import { db, functions } from "../firebase";

  // Finding #2: email -> user resolution runs server-side now (the open `users`
  // list rule is gone). See findUserByEmail in functions/index.js.
  const findUserByEmailFn = httpsCallable(functions, "findUserByEmail");

  // Walk a sessionData object's recordings (shape: { [itemId]: [recording,...] })
  // and append `viewerUid` to each matching mediaRecordings doc so Storage
  // rules allow the recipient to read the HLS playlist + poster.
  async function grantViewerOnSessionRecordings(sessionData, ownerUserId, viewerUid) {
    if (!sessionData?.recordings || !ownerUserId || !viewerUid) return;
    const ids = new Set();
    try {
      Object.values(sessionData.recordings).forEach((arr) => {
        (Array.isArray(arr) ? arr : [arr]).forEach((rec) => {
          if (rec && rec.id) ids.add(rec.id);
        });
      });
    } catch (e) { /* noop */ }

    await Promise.all(
      Array.from(ids).map(async (recId) => {
        try {
          const docRef = doc(db, 'users', ownerUserId, 'mediaRecordings', recId);
          await setDoc(docRef, { viewerUids: arrayUnion(viewerUid) }, { merge: true });
        } catch (e) {
          // Non-fatal: older recordings may not have a mediaRecordings doc.
          console.warn('Failed to extend viewerUids for recording', recId, e?.message);
        }
      })
    );
  }
  
  // Collections
  const RELATIONSHIPS_COLLECTION = "instructorStudentRelationships";
  const SHARED_SESSIONS_COLLECTION = "sharedPracticeSessions";
  const SESSION_MESSAGES_COLLECTION = "sessionMessages";
  
  // Student Groups Functions
  const STUDENT_GROUPS_COLLECTION = "studentGroups";

  // In-memory user profile cache (survives across component re-mounts within a session)
  const userProfileCache = new Map();
  const USER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

  export async function getCachedUserProfile(userId) {
    const cached = userProfileCache.get(userId);
    if (cached && (Date.now() - cached.fetchedAt) < USER_CACHE_TTL_MS) {
      return cached.data;
    }
    const userDoc = await getDoc(doc(db, 'users', userId));
    const data = userDoc.exists() ? userDoc.data() : null;
    userProfileCache.set(userId, { data, fetchedAt: Date.now() });
    return data;
  }

  export function clearUserProfileCache() {
    userProfileCache.clear();
  }
  
  // Relationship Functions
  export async function requestInstructorRelationship(studentId, instructorEmail, message = "") {
    // First find the instructor by email
    // Finding #2: resolve the instructor by email server-side (no users list).
    // Shape the result like the old query snapshot so the checks below are
    // unchanged (.empty / .docs[0].id).
    const _match = (await findUserByEmailFn({ email: instructorEmail })).data;
    const querySnapshot = { empty: !_match, docs: _match ? [{ id: _match.uid }] : [] };
    
    if (querySnapshot.empty) {
      throw new Error("No user found with that email");
    }
    
    const instructorData = querySnapshot.docs[0];
    const instructorId = instructorData.id;
    
    // Check if relationship already exists
    const relationshipsRef = collection(db, RELATIONSHIPS_COLLECTION);
    const existingQuery = query(
      relationshipsRef, 
      where("studentId", "==", studentId),
      where("instructorId", "==", instructorId)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error("Relationship request already exists");
    }
    
    // Create new relationship request
    return addDoc(relationshipsRef, {
      studentId,
      instructorId,
      message,
      status: "pending",
      initiatedBy: "student",
      createdAt: serverTimestamp(),
      lastInteractionDate: serverTimestamp()
    });
  }
  
  export async function requestStudentRelationship(instructorId, studentEmail, message = "") {
    try {
      // First find the student by email
      // Finding #2: resolve the student by email server-side (no users list).
      const _match = (await findUserByEmailFn({ email: studentEmail })).data;
      
      const querySnapshot = { empty: !_match, docs: _match ? [{ id: _match.uid }] : [] };
      
      if (querySnapshot.empty) {
        throw new Error("No user found with that email");
      }
      
      const studentData = querySnapshot.docs[0];
      const studentId = studentData.id;
      
      // Check if relationship already exists
      const relationshipsRef = collection(db, RELATIONSHIPS_COLLECTION);
      const existingQuery = query(
        relationshipsRef, 
        where("studentId", "==", studentId),
        where("instructorId", "==", instructorId)
      );
      
      const existingSnapshot = await getDocs(existingQuery);
      
      if (!existingSnapshot.empty) {
        throw new Error("Relationship request already exists");
      }
      
      // Create new relationship request
      const now = new Date();
      const relationshipData = {
        studentId,
        instructorId,
        message,
        status: "pending",
        initiatedBy: "instructor",
        createdAt: serverTimestamp(),
        createdAtFallback: Timestamp.fromDate(now),
        lastInteractionDate: serverTimestamp()
      };
      
      try {
        const docRef = await addDoc(relationshipsRef, relationshipData);
        return docRef;
      } catch (error) {
        console.error('Error adding document:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error creating student relationship:', error);
      throw error;
    }
  }
  
  export async function updateRelationshipStatus(relationshipId, newStatus) {
    const relationshipRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
    return updateDoc(relationshipRef, {
      status: newStatus,
      lastInteractionDate: serverTimestamp()
    });
  }
  
  export function getStudentRelationships(studentId, callback) {
    const relationshipsRef = collection(db, RELATIONSHIPS_COLLECTION);
    const q = query(
      relationshipsRef,
      where("studentId", "==", studentId),
      orderBy("lastInteractionDate", "desc"),
      limit(100)
    );
    
    return onSnapshot(q, callback);
  }
  
  export function getInstructorRelationships(instructorId, callback) {
    const relationshipsRef = collection(db, RELATIONSHIPS_COLLECTION);
    const q = query(
      relationshipsRef,
      where("instructorId", "==", instructorId),
      orderBy("lastInteractionDate", "desc"),
      limit(100)
    );
    
    return onSnapshot(q, callback);
  }
  
  // Shared Sessions Functions
  //
  // The session payload's `recordings` map may contain video entries that
  // reference Storage paths under `recordings/{studentId}/hls/...`. Those
  // paths are protected by a `viewerUids` allowlist on the owner's
  // `mediaRecordings` doc; we extend the allowlist here so the instructor
  // can actually read the shared HLS playlist + poster.
  export async function sharePracticeSession(sessionData, studentId, instructorId) {
    const sharedSessionsRef = collection(db, SHARED_SESSIONS_COLLECTION);

    const docData = {
      originalSessionId: sessionData.id,
      studentId,
      instructorId,
      fromUserId: studentId,
      toUserId: instructorId,
      sessionData,
      sharedAt: serverTimestamp(),
      reviewStatus: "unreviewed"
    };

    const created = await addDoc(sharedSessionsRef, docData);

    // Fire-and-forget: if this fails the recipient just won't be able to load
    // shared video. Audio remains viewable because it still uses the legacy
    // public-read storage path.
    grantViewerOnSessionRecordings(sessionData, studentId, instructorId)
      .catch((e) => console.warn('grantViewer on share failed', e));

    return created;
  }
  
  export function getSharedSessionsForStudent(studentId, callback) {
    const sharedSessionsRef = collection(db, SHARED_SESSIONS_COLLECTION);
    const q = query(
      sharedSessionsRef,
      where("studentId", "==", studentId),
      orderBy("sharedAt", "desc")
    );
    
    return onSnapshot(q, callback);
  }
  
  export function getSharedSessionsForInstructor(instructorId, callback) {
    const sharedSessionsRef = collection(db, SHARED_SESSIONS_COLLECTION);
    const q = query(
      sharedSessionsRef,
      where("instructorId", "==", instructorId),
      orderBy("sharedAt", "desc")
    );
    
    return onSnapshot(q, callback);
  }
  
  export async function updateSessionReviewStatus(sessionId, newStatus) {
    const sessionRef = doc(db, SHARED_SESSIONS_COLLECTION, sessionId);
    return updateDoc(sessionRef, {
      reviewStatus: newStatus
    });
  }
  
  // Session Messages Functions
  export async function addSessionMessage(sharedSessionId, senderId, content) {
    const messagesRef = collection(db, SESSION_MESSAGES_COLLECTION);
    
    return addDoc(messagesRef, {
      sharedSessionId,
      senderId,
      content,
      timestamp: serverTimestamp(),
      isRead: false
    });
  }
  
  export function getSessionMessages(sharedSessionId, callback) {
    const messagesRef = collection(db, SESSION_MESSAGES_COLLECTION);
    const q = query(
      messagesRef,
      where("sharedSessionId", "==", sharedSessionId),
      orderBy("timestamp", "asc")
    );
    
    return onSnapshot(q, callback);
  }
  
  export async function markMessageAsRead(messageId) {
    const messageRef = doc(db, SESSION_MESSAGES_COLLECTION, messageId);
    return updateDoc(messageRef, {
      isRead: true
    });
  }
  
  export async function getUnreadMessageCount(userId, callback) {
    const messagesRef = collection(db, SESSION_MESSAGES_COLLECTION);
    
    // Get sessions where user is either student or instructor
    const sharedSessionsRef = collection(db, SHARED_SESSIONS_COLLECTION);
    const sessionsQueryStudent = query(
      sharedSessionsRef,
      where("studentId", "==", userId)
    );
    const sessionsQueryInstructor = query(
      sharedSessionsRef,
      where("instructorId", "==", userId)
    );
    
    const [studentSessions, instructorSessions] = await Promise.all([
      getDocs(sessionsQueryStudent),
      getDocs(sessionsQueryInstructor)
    ]);
    
    // Combine session IDs
    const sessionIds = [
      ...studentSessions.docs.map(doc => doc.id),
      ...instructorSessions.docs.map(doc => doc.id)
    ];
    
    // If no sessions, return 0
    if (sessionIds.length === 0) {
      callback({ count: 0 });
      return () => {};
    }
    
    // Query for unread messages where user is not the sender
    const q = query(
      messagesRef,
      where("sharedSessionId", "in", sessionIds),
      where("senderId", "!=", userId),
      where("isRead", "==", false)
    );
    
    return onSnapshot(q, (snapshot) => {
      callback({ count: snapshot.docs.length });
    });
  }
  
  // Student Groups Functions
  export async function createStudentGroup(instructorId, groupName, description = "") {
    const groupsRef = collection(db, STUDENT_GROUPS_COLLECTION);
    
    // Check if group name already exists for this instructor
    const existingQuery = query(
      groupsRef,
      where("instructorId", "==", instructorId),
      where("name", "==", groupName)
    );
    
    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error("A group with this name already exists");
    }
    
    return addDoc(groupsRef, {
      instructorId,
      name: groupName,
      description,
      studentIds: [],
      createdAt: serverTimestamp(),
      lastModified: serverTimestamp()
    });
  }
  
  export async function updateStudentGroup(groupId, updates) {
    const groupRef = doc(db, STUDENT_GROUPS_COLLECTION, groupId);
    return updateDoc(groupRef, {
      ...updates,
      lastModified: serverTimestamp()
    });
  }
  
  export async function deleteStudentGroup(groupId) {
    const groupRef = doc(db, STUDENT_GROUPS_COLLECTION, groupId);
    return updateDoc(groupRef, {
      deleted: true,
      lastModified: serverTimestamp()
    });
  }
  
  export function getInstructorGroups(instructorId, callback) {
    const groupsRef = collection(db, STUDENT_GROUPS_COLLECTION);
    const q = query(
      groupsRef,
      where("instructorId", "==", instructorId),
      orderBy("name")
    );
    
    return onSnapshot(q, (snapshot) => {
      // Filter out deleted groups in JavaScript instead of requiring a composite index
      const filteredSnapshot = {
        docs: snapshot.docs.filter(doc => {
          const data = doc.data();
          return !data.deleted;
        })
      };
      callback(filteredSnapshot);
    });
  }
  
  export async function assignStudentToGroup(groupId, studentId) {
    const groupRef = doc(db, STUDENT_GROUPS_COLLECTION, groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error("Group not found");
    }
    
    const currentStudentIds = groupDoc.data().studentIds || [];
    if (currentStudentIds.includes(studentId)) {
      throw new Error("Student is already in this group");
    }
    
    return updateDoc(groupRef, {
      studentIds: [...currentStudentIds, studentId],
      lastModified: serverTimestamp()
    });
  }
  
  export async function removeStudentFromGroup(groupId, studentId) {
    const groupRef = doc(db, STUDENT_GROUPS_COLLECTION, groupId);
    const groupDoc = await getDoc(groupRef);
    
    if (!groupDoc.exists()) {
      throw new Error("Group not found");
    }
    
    const currentStudentIds = groupDoc.data().studentIds || [];
    const updatedStudentIds = currentStudentIds.filter(id => id !== studentId);
    
    return updateDoc(groupRef, {
      studentIds: updatedStudentIds,
      lastModified: serverTimestamp()
    });
  }

  // Instructor Data Access Functions

  export async function updateAccessLevel(relationshipId, studentId, instructorId, newLevel) {
    const batch = writeBatch(db);

    const relationshipRef = doc(db, RELATIONSHIPS_COLLECTION, relationshipId);
    batch.update(relationshipRef, {
      accessLevel: newLevel,
      lastInteractionDate: serverTimestamp()
    });

    const accessRef = doc(db, 'users', studentId, 'instructorAccess', instructorId);
    if (newLevel === 'full') {
      batch.set(accessRef, {
        accessLevel: 'full',
        grantedAt: serverTimestamp()
      });
    } else {
      batch.delete(accessRef);
    }

    return batch.commit();
  }

  export async function getStudentPracticeDays(studentId) {
    const daysRef = collection(db, 'users', studentId, 'practiceDays');
    const q = query(daysRef, orderBy('dayKey', 'desc'), limit(365));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  export async function getStudentSessionHistory(studentId, limitCount = 200) {
    const sessionsRef = collection(db, 'users', studentId, 'sessionHistory');
    const q = query(sessionsRef, orderBy('date', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .filter(d => d.data()?.deleted !== true) // exclude soft-deleted sessions (instructor view)
      .map(d => ({ id: d.id, ...d.data() }));
  }

  export async function getStudentTagMappings(studentId) {
    const tagDoc = await getDoc(doc(db, 'users', studentId, 'settings', 'tagMappings'));
    if (!tagDoc.exists()) return {};
    return tagDoc.data().mappings || {};
  }